import React, { useCallback, useEffect, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  ArcElement, Tooltip, Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Tooltip, Filler);

// Stage 2: the board-ready dark "Talent Market Intelligence Report", rendered
// from GET /api/monitoring/projects/:id/intelligence and exportable to PDF.
// White-label: falls back to the MarketMapr wordmark unless a client logo is set.

interface IntelligenceReportProps {
  projectId: number;
  projectName?: string;
  clientLogoUrl?: string | null; // optional white-label logo (data/URL)
  onClose: () => void;
}

// --- Dark palette (inline so capture is theme-independent) ---
const C = {
  bg: '#0a0e1a',
  panel: '#111827',
  panel2: '#0f1626',
  border: '#1e293b',
  text: '#e2e8f0',
  heading: '#f8fafc',
  muted: '#94a3b8',
  faint: '#64748b',
  teal: '#2dd4bf',
  purple: '#a78bfa',
  green: '#4ade80',
  red: '#f87171',
  amber: '#fbbf24',
  blue: '#60a5fa',
};
const SENIORITY_COLORS: Record<string, string> = {
  'Individual Contributor': '#2dd4bf',
  Manager: '#a78bfa',
  Director: '#fbbf24',
  'VP+': '#f472b6',
  Unknown: '#475569',
};
const FUNCTION_COLOR = '#2dd4bf';

// Signal types the user can append as detailed pages after the intelligence summary.
const BACKFILL_KEY = '__backfills__';
const SIGNAL_TYPES: { value: string; label: string; color: string }[] = [
  { value: 'job_change', label: 'Job Changes', color: '#60a5fa' },
  { value: BACKFILL_KEY, label: 'Backfill Opportunities', color: '#fb923c' },
  { value: 'open_to_work', label: 'Open to Work', color: '#4ade80' },
  { value: 'critical_risk_candidate', label: 'Critical Flight Risk', color: '#f87171' },
  { value: 'high_risk_candidate', label: 'High Flight Risk', color: '#f472b6' },
  { value: 'promotion', label: 'Promotions', color: '#a78bfa' },
  { value: 'profile_change', label: 'Profile Updates', color: '#94a3b8' },
  { value: 'company_hiring_trend', label: 'Company Trends', color: '#818cf8' },
];

// One-line detail for an appended statement signal.
function signalDetailStr(s: any): string {
  if (s.from_company || s.to_company) return `${s.from_company || '?'} → ${s.to_company || '?'}${s.move_date ? ` (${s.move_date})` : ''}`;
  if (s.risk_score != null) return `Risk ${s.risk_score}${s.company ? ` · ${s.company}` : ''}`;
  if (s.detail) return s.detail;
  if (s.company) return s.company;
  return s.title || '';
}

function isoDay(d: Date) { return d.toISOString().slice(0, 10); }
function fmtNum(n: number) { return (n ?? 0).toLocaleString(); }
function quarterLabel(fromIso: string, toIso: string) {
  const f = new Date(fromIso), t = new Date(toIso);
  const fmt = (d: Date) => d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  return `${fmt(f)} – ${fmt(t)}`.toUpperCase();
}

const Delta: React.FC<{ pct?: number; yrs?: number }> = ({ pct, yrs }) => {
  const v = pct != null ? pct : (yrs != null ? yrs : 0);
  const up = v > 0;
  const flat = v === 0;
  const color = flat ? C.muted : up ? C.green : C.red;
  const arrow = flat ? '' : up ? '↑ ' : '↓ ';
  const suffix = yrs != null ? ' yrs' : '%';
  const num = yrs != null ? Math.abs(yrs) : Math.abs(v);
  return <span style={{ color, fontSize: 11, fontWeight: 600 }}>{arrow}{num}{suffix} vs prev</span>;
};

const IntelligenceReport: React.FC<IntelligenceReportProps> = ({ projectId, projectName, clientLogoUrl, onClose }) => {
  const today = new Date();
  const quarterAgo = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
  const [from, setFrom] = useState(isoDay(quarterAgo));
  const [to, setTo] = useState(isoDay(today));
  const [data, setData] = useState<any>(null);
  const [statement, setStatement] = useState<any>(null);
  const [appendixTypes, setAppendixTypes] = useState<string[]>([]);
  const [logoUrl, setLogoUrl] = useState<string | null>(clientLogoUrl || null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const generate = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem('token');
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      const typesParam = appendixTypes.length ? `&types=${appendixTypes.join(',')}` : '';
      const [intelRes, stmtRes] = await Promise.all([
        fetch(`/api/monitoring/projects/${projectId}/intelligence?from=${from}&to=${to}`, auth),
        fetch(`/api/monitoring/projects/${projectId}/report?from=${from}&to=${to}${typesParam}`, auth),
      ]);
      if (!intelRes.ok) throw new Error(`Request failed (${intelRes.status})`);
      setData(await intelRes.json());
      setStatement(stmtRes.ok ? await stmtRes.json() : null);
    } catch (e: any) {
      setError(e?.message || 'Failed to build report'); setData(null);
    } finally { setLoading(false); }
  }, [projectId, from, to, appendixTypes]);

  useEffect(() => { generate(); /* eslint-disable-next-line */ }, []);

  // Re-generate when the appendix selection changes (skip the initial mount).
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    generate();
    // eslint-disable-next-line
  }, [appendixTypes]);

  // Fetch the team/user white-label logo once (falls back to the prop / wordmark).
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/monitoring/report-branding', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && d.logoUrl) setLogoUrl(d.logoUrl); })
      .catch(() => {});
  }, []);

  const toggleAppendix = (v: string) =>
    setAppendixTypes(prev => (prev.indexOf(v) >= 0 ? prev.filter(t => t !== v) : prev.concat(v)));

  // Appendix sections (selected signal types + backfill opportunities), shared
  // by the on-screen preview and the native PDF renderer.
  const buildAppendixSections = () => {
    const sections: { label: string; color: string; rows: { date: string; name: string; title: string | null; detail: string; url: string | null }[] }[] = [];
    for (const t of SIGNAL_TYPES) {
      if (appendixTypes.indexOf(t.value) < 0) continue;
      if (t.value === BACKFILL_KEY) {
        const deps = (statement && statement.departures) || (data && data.departures) || [];
        const rows: any[] = [];
        deps.forEach((g: any) => (g.people || []).forEach((p: any) => rows.push({
          date: p.date, name: p.name, title: p.title,
          detail: `left ${g.company}${p.to ? ` → joined ${p.to}` : ''}`, url: p.linkedin || null,
        })));
        if (rows.length) sections.push({ label: t.label, color: t.color, rows });
      } else {
        const list = ((statement && statement.signals) || []).filter((s: any) => s.type === t.value);
        if (list.length) sections.push({
          label: t.label, color: t.color,
          rows: list.map((s: any) => ({
            date: s.date, name: s.candidate_name || s.company || '—',
            title: s.candidate_job_title, detail: signalDetailStr(s), url: s.candidate_linkedin_url || null,
          })),
        });
      }
    }
    return sections;
  };

  const exportPDF = async () => {
    if (!canvasRef.current) return;
    setExporting(true);
    try {
      const el = canvasRef.current;
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: C.bg, useCORS: true, logging: false });
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgData = canvas.toDataURL('image/png');
      // Fit the WHOLE main report onto one page. It used to be sliced across A4
      // pages (position -= pageH each loop), which cut whatever panel or text
      // line sat on the page boundary — the recurring "text cut off". The main
      // report is a fixed one-page design, so scale it to fit the page instead:
      // fit to width, and if that overflows the page height, fit to height and
      // centre horizontally. Nothing is sliced, so nothing is cut.
      let imgW = pageW;
      let imgH = (canvas.height * imgW) / canvas.width;
      let x = 0;
      if (imgH > pageH) {
        imgH = pageH;
        imgW = (canvas.width * imgH) / canvas.height;
        x = (pageW - imgW) / 2;
      }
      pdf.addImage(imgData, 'PNG', x, 0, imgW, imgH);

      // Appendix rendered as NATIVE PDF text (not html2canvas): nothing clips,
      // and the LinkedIn chips are real, clickable links.
      const sections = buildAppendixSections();
      if (sections.length > 0) {
        const margin = 12;
        const bottom = pageH - 12;
        const dark = () => { pdf.setFillColor(10, 14, 26); pdf.rect(0, 0, pageW, pageH, 'F'); };
        const hexToRgb = (h: string): [number, number, number] => {
          const n = parseInt(h.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
        };
        pdf.addPage(); dark();
        let y = margin + 6;
        pdf.setTextColor(248, 250, 252); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15);
        pdf.text('Detailed Monitoring Changes', margin, y); y += 8;
        const ensure = (h: number) => { if (y + h > bottom) { pdf.addPage(); dark(); y = margin + 6; } };
        for (const sec of sections) {
          const [r, g, b] = hexToRgb(sec.color);
          ensure(14);
          pdf.setFillColor(Math.round(r * 0.22 + 8), Math.round(g * 0.22 + 11), Math.round(b * 0.22 + 20));
          pdf.roundedRect(margin, y, pageW - margin * 2, 8, 1.5, 1.5, 'F');
          pdf.setTextColor(r, g, b); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
          pdf.text(sec.label.toUpperCase(), margin + 3, y + 5.4);
          pdf.text(String(sec.rows.length), pageW - margin - 3, y + 5.4, { align: 'right' });
          y += 11;
          pdf.setFont('helvetica', 'normal');
          for (const row of sec.rows) {
            const full = `${row.name}${row.title ? ` · ${row.title}` : ''}${row.detail ? ` — ${row.detail}` : ''}`;
            const wrapped = pdf.splitTextToSize(full, pageW - margin * 2 - 26);
            const rowH = Math.max(5.5, wrapped.length * 4 + 1.5);
            ensure(rowH);
            pdf.setTextColor(148, 163, 184); pdf.setFontSize(7.5);
            pdf.text(new Date(row.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }), margin, y + 2.8);
            pdf.setTextColor(226, 232, 240); pdf.setFontSize(8.5);
            pdf.text(wrapped, margin + 16, y + 2.8);
            if (row.url) {
              const lx = pageW - margin - 7, ly = y - 0.6;
              pdf.setFillColor(10, 102, 194); pdf.roundedRect(lx, ly, 7, 4.6, 1, 1, 'F');
              pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(6);
              pdf.text('in', lx + 1.9, ly + 3.2);
              pdf.setFont('helvetica', 'normal');
              pdf.link(lx, ly, 7, 4.6, { url: row.url });
            }
            y += rowH;
          }
          y += 3;
        }
      }

      pdf.save(`talent-intelligence-${(projectName || 'project').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${from}_to_${to}.pdf`);
    } catch (e: any) {
      setError(e?.message || 'PDF export failed');
    } finally { setExporting(false); }
  };

  // --- Small presentational helpers ---
  const Panel: React.FC<{ title?: string; subtitle?: string; children: React.ReactNode; style?: React.CSSProperties }> = ({ title, subtitle, children, style }) => (
    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, ...style }}>
      {title && <div style={{ color: C.heading, fontSize: 13, fontWeight: 700, letterSpacing: 0.3 }}>{title}</div>}
      {subtitle && <div style={{ color: C.faint, fontSize: 11, marginBottom: 10 }}>{subtitle}</div>}
      {!subtitle && title && <div style={{ marginBottom: 10 }} />}
      {children}
    </div>
  );

  const BarRow: React.FC<{ label: string; value: number; max: number; color: string; suffix?: string }> = ({ label, value, max, color, suffix }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingBottom: 2 }}>
      <div style={{ width: 96, color: C.muted, fontSize: 11, lineHeight: '18px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
      <div style={{ flex: 1, background: '#0b1120', borderRadius: 4, height: 9 }}>
        <div style={{ width: `${max > 0 ? Math.max((value / max) * 100, 2) : 0}%`, background: color, height: 9, borderRadius: 4 }} />
      </div>
      <div style={{ width: 40, textAlign: 'right', color: C.text, fontSize: 11, lineHeight: '18px', fontWeight: 600 }}>{suffix ? `${value}${suffix}` : fmtNum(value)}</div>
    </div>
  );

  const renderCanvas = () => {
    if (!data) return null;
    const k = data.kpis || {};
    const maxTs = Math.max(1, ...(data.timeSeries || []).map((p: any) => p.count));
    const arrivals = data.arrivals || [];
    const maxArr = Math.max(1, ...arrivals.map((a: any) => a.count));
    const topHiring = data.topHiring || [];
    const topLosing = data.topLosing || [];
    const byFunction = data.byFunction || [];
    const maxFn = Math.max(1, ...byFunction.map((f: any) => f.count));
    const byRegion = data.byRegion || [];
    const maxReg = Math.max(1, ...byRegion.map((r: any) => r.count));
    const bySeniority = (data.bySeniority || []).filter((s: any) => s.count > 0);
    const tenureBySeniority = data.tenureBySeniority || [];

    const lineData = {
      labels: (data.timeSeries || []).map((p: any) => p.label),
      datasets: [{
        data: (data.timeSeries || []).map((p: any) => p.count),
        borderColor: C.teal, borderWidth: 2, tension: 0.4,
        pointBackgroundColor: C.teal, pointRadius: 3,
        fill: true, backgroundColor: 'rgba(45,212,191,0.12)',
      }],
    };
    const lineOpts: any = {
      responsive: true, maintainAspectRatio: false, animation: false,
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      scales: {
        x: { grid: { color: 'rgba(148,163,184,0.08)' }, ticks: { color: C.faint, font: { size: 9 } } },
        y: { grid: { color: 'rgba(148,163,184,0.08)' }, ticks: { color: C.faint, font: { size: 9 } }, beginAtZero: true },
      },
    };
    const donutData = {
      labels: bySeniority.map((s: any) => s.bucket),
      datasets: [{
        data: bySeniority.map((s: any) => s.count),
        backgroundColor: bySeniority.map((s: any) => SENIORITY_COLORS[s.bucket] || C.faint),
        borderColor: C.panel, borderWidth: 2,
      }],
    };
    const donutOpts: any = {
      responsive: true, maintainAspectRatio: false, animation: false, cutout: '62%',
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
    };
    const totalSen = bySeniority.reduce((a: number, s: any) => a + s.count, 0) || 1;

    const kpiTiles = [
      { label: 'Total Moves', value: fmtNum(k.totalMoves?.value), delta: <Delta pct={k.totalMoves?.deltaPct} />, color: C.teal },
      { label: 'Companies Hiring', value: fmtNum(k.companiesHiring?.value), delta: <Delta pct={k.companiesHiring?.deltaPct} />, color: C.purple },
      { label: 'Avg Tenure (Movers)', value: `${(k.avgMoverTenureYrs?.value ?? 0).toFixed(1)} yrs`, delta: <Delta yrs={k.avgMoverTenureYrs?.deltaYrs} />, color: C.amber },
      { label: 'Open to Work', value: fmtNum(k.openToWork?.value), delta: <Delta pct={k.openToWork?.deltaPct} />, color: C.green },
    ];

    return (
      <div ref={canvasRef} style={{ background: C.bg, padding: 28, paddingBottom: 34, width: 860, boxSizing: 'border-box', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.6 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 26 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {logoUrl
              ? <img src={logoUrl} alt="logo" style={{ height: 36, maxWidth: 200, objectFit: 'contain' }} crossOrigin="anonymous" />
              : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 26, height: 26, borderRadius: 7, background: `linear-gradient(135deg, ${C.teal}, ${C.purple})` }} />
                  <div>
                    <div style={{ color: C.heading, fontWeight: 800, fontSize: 15, letterSpacing: 0.5 }}>MARKETMAPR</div>
                    <div style={{ color: C.teal, fontSize: 8, letterSpacing: 2 }}>TALENT INTELLIGENCE</div>
                  </div>
                </div>
              )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: C.muted, fontSize: 10, letterSpacing: 2, fontWeight: 600 }}>EXECUTIVE TALENT MOVEMENT REPORT</div>
            <div style={{ color: C.faint, fontSize: 10, letterSpacing: 1 }}>{quarterLabel(data.period.from, data.period.to)}</div>
          </div>
        </div>

        {/* Title */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ color: C.heading, fontSize: 34, fontWeight: 800, lineHeight: 1.25 }}>The Talent Market</div>
          <div style={{ color: C.heading, fontSize: 34, fontWeight: 800, lineHeight: 1.25 }}>Intelligence Report</div>
          <div style={{ color: C.teal, fontSize: 18, fontWeight: 600, marginTop: 6 }}>{projectName || data.project.name} · Executive Summary</div>
          <div style={{ color: C.muted, fontSize: 12, marginTop: 8, maxWidth: 440 }}>
            An analysis of candidate movement, hiring trends, and workforce dynamics across the market.
          </div>
        </div>

        {/* KPI tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
          {kpiTiles.map(t => (
            <div key={t.label} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
              <div style={{ color: C.faint, fontSize: 9, letterSpacing: 1, fontWeight: 700 }}>{t.label.toUpperCase()}</div>
              <div style={{ color: t.color, fontSize: 26, fontWeight: 800, margin: '4px 0 3px' }}>{t.value}</div>
              {t.delta}
            </div>
          ))}
        </div>

        {/* Row: line + destinations + top hiring */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          <Panel title="Talent Movement Over Time" subtitle="Total candidate moves by month">
            <div style={{ height: 130 }}><Line data={lineData} options={lineOpts} /></div>
          </Panel>
          <Panel title="Where Talent Is Going" subtitle="Top destinations by moves">
            {arrivals.slice(0, 6).map((a: any) => <BarRow key={a.company} label={a.company} value={a.count} max={maxArr} color={C.teal} />)}
          </Panel>
          <Panel title="Top Hiring Companies" subtitle="Highest net headcount growth">
            {topHiring.slice(0, 6).map((c: any) => (
              <div key={c.company} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: C.text, fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>{c.company}</span>
                <span style={{ color: C.green, fontSize: 11, fontWeight: 700 }}>+{c.net}</span>
              </div>
            ))}
          </Panel>
        </div>

        {/* Row: flow + losing + seniority donut */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          <Panel title="Talent Movement Flow" subtitle="Top company-to-company moves">
            {(data.flow || []).slice(0, 8).map((e: any, i: number) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 10 }}>
                <span style={{ color: C.muted, width: 110, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'right' }}>{e.from}</span>
                <span style={{ color: C.teal }}>→</span>
                <span style={{ color: C.text, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.to}</span>
                <span style={{ color: C.purple, fontWeight: 700 }}>{e.count}</span>
              </div>
            ))}
          </Panel>
          <Panel title="Companies Losing Talent" subtitle="By net departures">
            {topLosing.slice(0, 6).map((c: any) => (
              <div key={c.company} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: C.text, fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>{c.company}</span>
                <span style={{ color: C.red, fontSize: 11, fontWeight: 700 }}>-{c.exits}</span>
              </div>
            ))}
          </Panel>
          <Panel title="Movers by Seniority" subtitle="Monitored candidates who moved, by title">
            <div style={{ height: 100, position: 'relative' }}>
              <Doughnut data={donutData} options={donutOpts} />
            </div>
            <div style={{ marginTop: 8 }}>
              {bySeniority.map((s: any) => (
                <div key={s.bucket} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, marginBottom: 3 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 8, background: SENIORITY_COLORS[s.bucket] || C.faint }} />
                  <span style={{ color: C.muted, flex: 1 }}>{s.bucket}</span>
                  <span style={{ color: C.text, fontWeight: 600 }}>{Math.round((s.count / totalSen) * 100)}%</span>
                </div>
              ))}
            </div>
          </Panel>
        </div>

        {/* Row: tenure by seniority + function + region */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
          <Panel title="Average Tenure of Movers" subtitle="By seniority level">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {tenureBySeniority.map((t: any) => (
                <div key={t.bucket} style={{ background: C.panel2, borderRadius: 8, padding: 8 }}>
                  <div style={{ color: C.faint, fontSize: 8, letterSpacing: 0.5 }}>{t.bucket.toUpperCase()}</div>
                  <div style={{ color: SENIORITY_COLORS[t.bucket] || C.text, fontSize: 18, fontWeight: 800 }}>{(t.avgYears ?? 0).toFixed(1)} <span style={{ fontSize: 10, color: C.muted }}>yrs</span></div>
                </div>
              ))}
            </div>
          </Panel>
          <Panel title="Moves by Function" subtitle="Breakdown by function">
            {byFunction.slice(0, 6).map((f: any) => <BarRow key={f.fn} label={f.fn} value={f.pct} max={100} color={FUNCTION_COLOR} suffix="%" />)}
          </Panel>
          <Panel title="Top Movement Regions" subtitle="By number of moves">
            {byRegion.slice(0, 6).map((r: any) => (
              <div key={r.region} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ color: C.text, fontSize: 11 }}>{r.region}</span>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ color: C.text, fontSize: 11, fontWeight: 700 }}>{fmtNum(r.count)}</span>
                  <span style={{ color: r.deltaPct >= 0 ? C.green : C.red, fontSize: 9 }}>{r.deltaPct >= 0 ? '↑' : '↓'}{Math.abs(r.deltaPct)}%</span>
                </span>
              </div>
            ))}
          </Panel>
        </div>

        {/* Key insights + footer */}
        <div style={{ background: C.panel2, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 12 }}>
          <div style={{ color: C.heading, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Key Insights</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {(data.insights || []).map((ins: string, i: number) => (
              <div key={i} style={{ display: 'flex', gap: 8, color: C.muted, fontSize: 11 }}>
                <span style={{ color: C.teal }}>▹</span><span>{ins}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', color: C.faint, fontSize: 9, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
          <span>{logoUrl ? '' : 'MARKETMAPR · '}TALENT INTELLIGENCE THAT DRIVES DECISIONS</span>
          <span>Based on monitored candidates · seniority/function/region are heuristic</span>
        </div>
      </div>
    );
  };

  // Appendix preview (shown below the summary in the modal; NOT html2canvas-
  // captured — the PDF renders it natively). LinkedIn chips are clickable here.
  const renderAppendixPreview = () => {
    const sections = buildAppendixSections();
    if (sections.length === 0) return null;
    return (
      <div style={{ background: C.bg, padding: 28, paddingTop: 10, width: 860, boxSizing: 'border-box', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.5 }}>
        <div style={{ color: C.heading, fontSize: 18, fontWeight: 800, marginBottom: 2 }}>Detailed Monitoring Changes</div>
        <div style={{ color: C.faint, fontSize: 11, marginBottom: 14 }}>Every selected change in the period · LinkedIn links are clickable in the PDF.</div>
        {sections.map(sec => (
          <div key={sec.label} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', background: sec.color + '1f', border: `1px solid ${sec.color}55`, borderRadius: 8, padding: '6px 12px', marginBottom: 8 }}>
              <span style={{ color: sec.color, fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}>{sec.label.toUpperCase()}</span>
              <span style={{ color: sec.color, fontSize: 12, fontWeight: 700 }}>{sec.rows.length}</span>
            </div>
            {sec.rows.map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, padding: '3px 4px', lineHeight: 1.6 }}>
                <span style={{ color: C.faint, width: 48, flexShrink: 0 }}>{new Date(r.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                <span style={{ color: C.text, flex: 1 }}>
                  <strong style={{ color: C.heading }}>{r.name}</strong>
                  {r.title ? <span style={{ color: C.muted }}> · {r.title}</span> : null}
                  {r.detail ? <span style={{ color: C.muted }}> — {r.detail}</span> : null}
                </span>
                {r.url && (
                  <a href={r.url} target="_blank" rel="noreferrer" style={{ flexShrink: 0, background: '#0a66c2', color: '#fff', fontSize: 8, fontWeight: 700, borderRadius: 3, padding: '1px 5px', textDecoration: 'none' }}>in</a>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl my-6" onClick={e => e.stopPropagation()}>
        {/* Controls bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-gray-200">
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-[10px] font-medium text-gray-500">From</label>
              <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500">To</label>
              <input type="date" value={to} min={from} onChange={e => setTo(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm" />
            </div>
            <button onClick={generate} disabled={loading} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Building…' : 'Generate'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportPDF} disabled={!data || loading || exporting} className="px-4 py-1.5 bg-gray-900 text-white text-sm rounded hover:bg-black disabled:opacity-50">
              {exporting ? 'Exporting…' : 'Download PDF'}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none px-1">&times;</button>
          </div>
        </div>

        {/* Append detailed monitoring changes onto the report */}
        <div className="flex flex-wrap items-center gap-1.5 px-5 py-2 border-b border-gray-100 bg-gray-50">
          <span className="text-[11px] text-gray-500 mr-1">Append detailed changes:</span>
          {SIGNAL_TYPES.map(t => {
            const active = appendixTypes.indexOf(t.value) >= 0;
            return (
              <button key={t.value} onClick={() => toggleAppendix(t.value)}
                className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors ${active ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-600 hover:border-blue-400'}`}>
                {t.label}
              </button>
            );
          })}
        </div>

        {error && <div className="px-5 py-2 text-sm text-red-600">{error}</div>}

        {/* Report canvas (dark), scrollable preview */}
        <div className="p-4 overflow-x-auto" style={{ background: '#e5e7eb' }}>
          {loading && <div className="text-sm text-gray-500 p-8 text-center">Building intelligence report…</div>}
          {!loading && data && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {renderCanvas()}
              {renderAppendixPreview()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntelligenceReport;
